import { AudioLines, Captions } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip } from "@/components/ui/misc";
import { CALL_STATUS_LABELS, callAgentVerb, formatSeconds, type CallActivity } from "@/lib/conversations";
import { cn, formatDateTime } from "@/lib/utils";
import { CallAudio } from "@/features/call-audio";
import { CALL_STATUS_TONES, callLook } from "@/features/conversations/channel-ui";

/**
 * A call in the thread: which way, how it ended, who took it, how long, and the line the record's
 * timeline says about it. A call that connected offers its Recording and its Transcript, each off
 * with the reason when this reader has none. Either opens one dialog, where `CallAudio` plays the
 * file with the session's token — a plain `<audio src>` would get a 401 — and shows the transcript
 * under it; nothing is fetched until someone asks, so scrolling a thread downloads no recording.
 */
export function CallActivityCard({ call, contactName }: { call: CallActivity; contactName: string }) {
  const [open, setOpen] = React.useState(false);
  const { icon: Icon, tone } = callLook(call);
  const connected = call.status === "completed";
  const way = call.direction === "inbound" ? "Inbound call" : "Outbound call";
  const offersMedia = connected || call.hasRecording || call.hasTranscript;
  const recording = call.callId !== null && call.hasRecording;
  const transcript = call.callId !== null && call.hasTranscript;
  return (
    <div className="w-full max-w-md rounded-xl border border-border bg-surface p-3 shadow-xs">
      <div className="flex items-center gap-2">
        <span className={cn("flex size-7 shrink-0 items-center justify-center rounded-full bg-surface-sunken", tone)}>
          <Icon className="size-3.5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text">{way}</p>
          {call.forCallback && (
            // The call the dialer made for a callback, connected or not (ADR-162 in `platform`).
            <p className="truncate text-2xs font-medium text-accent-11">{call.forCallback}</p>
          )}
          <p className="truncate text-2xs text-text-muted">
            {call.agent ? `${callAgentVerb(call)} ${call.agent.name} · ` : ""}
            {connected && call.durationSeconds !== null ? formatSeconds(call.durationSeconds) : connected ? "Connected" : "Not connected"}
          </p>
        </div>
        {/* Only when something went wrong. A call that connected says so three times over
            already — the duration beside the rep's name, and "Talked 5:17 with …" in the line
            below — and a badge on every card is a badge that marks nothing. The ones left are
            the ones a reader scans a thread for. */}
        {!connected && (
          <Badge tone={CALL_STATUS_TONES[call.status]} size="sm">
            {CALL_STATUS_LABELS[call.status]}
          </Badge>
        )}
      </div>

      {(call.summary || call.outcome) && (
        <p className="mt-2 text-xs text-text-muted">
          {call.summary}
          {call.outcome && !call.summary.includes(call.outcome) && ` · ${call.outcome}`}
        </p>
      )}

      {offersMedia && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 border-t border-border pt-3">
          <MediaButton on={recording} why="No recording for this call" onClick={() => setOpen(true)}>
            <AudioLines />
            Recording
          </MediaButton>
          <MediaButton
            on={transcript}
            why={recording ? "This call was not transcribed" : "No recording, so no transcript"}
            onClick={() => setOpen(true)}
          >
            <Captions />
            View Transcript
          </MediaButton>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{way}</DialogTitle>
                <DialogDescription>
                  With {contactName}, {formatDateTime(call.at)}
                  {call.durationSeconds !== null ? ` · ${formatSeconds(call.durationSeconds)}` : ""}
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                {open && <CallAudio callId={call.callId ?? undefined} hasTranscript={call.hasTranscript} />}
              </DialogBody>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}

/** A link-style button, or the same button off with why when this reader has nothing to open. */
function MediaButton({
  on, why, onClick, children,
}: {
  on: boolean;
  why: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  if (on) {
    return (
      <Button variant="link" size="sm" className="h-auto px-0" onClick={onClick}>
        {children}
      </Button>
    );
  }
  return (
    <Tooltip content={why}>
      <span tabIndex={0} className="inline-block">
        <Button variant="link" size="sm" className="h-auto px-0" disabled>
          {children}
        </Button>
      </span>
    </Tooltip>
  );
}
